import { ArrowLeft, ArrowRight } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion } from 'components'
import { VehicleCheckInDetailsDto } from 'dtos'
import CheckInReviewItem from './CheckInReviewItem'
import { VehicleCheckInPhotosProps } from './index'
import VehicleCheckInStepper from './VehicleCheckInStepper'

interface Step4CheckInReviewProps extends VehicleCheckInPhotosProps {
  onBack: () => void
  onSubmit: () => void
  onSubmitAndRecordVehicleDamage: () => void
  vehicleCheckInDetails: VehicleCheckInDetailsDto
  vehicleGuid?: string
}

export default function Step4CheckInReview({
  onBack,
  onSubmit,
  onSubmitAndRecordVehicleDamage,
  vehicleCheckInDetails,
  vehicleGuid,
  manufacturersLabelPhoto,
  setManufacturersLabelPhoto,
  tirePressureLabelPhoto,
  setTirePressureLabelPhoto,
  importerDoorLabelPhoto,
  setImporterDoorLabelPhoto,
  comboPhoto,
  setComboPhoto,
  checkInClusterPhoto,
  setCheckInClusterPhoto,
  clusterWithOdometerPhoto,
  setClusterWithOdometerPhoto,
  driverAirbagPhoto,
  setDriverAirbagPhoto,
  backupCamPhoto,
  setBackupCamPhoto,
  airbagTellTalePhoto,
  setAirbagTellTalePhoto,
  passengerAirbagPhoto,
  setPassengerAirbagPhoto,
  vinOnDashboardPhoto,
  setVinOnDashboardPhoto,
  roofPhoto,
  setRoofPhoto,
  frontCornerPhoto,
  setFrontCornerPhoto,
  frontHeadOnPhoto,
  setFrontHeadOnPhoto,
  rearCornerPhoto,
  setRearCornerPhoto,
  rearHeadOnPhoto,
  setRearHeadOnPhoto
}: Step4CheckInReviewProps) {
  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <VehicleCheckInStepper activeStep={3} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='body1'>
              Vehicle Details
            </Typography>
          </Grid>

          <CheckInReviewItem complete label='Vehicle Details' />

          <Grid item xs={12} mt={3}>
            <Typography fontWeight='bold' variant='body1'>
              Print Labels
            </Typography>
          </Grid>

          <CheckInReviewItem complete label='Importer Door Label' />

          <CheckInReviewItem complete disabled={!vehicleCheckInDetails.convertCluster} label='Convert Cluster Label' />

          <CheckInReviewItem complete disabled={!vehicleCheckInDetails.convertCluster} label='Program Cluster Label' />

          <CheckInReviewItem complete label='Window Label' />

          <Grid item xs={12} mt={3}>
            <Typography fontWeight='bold' variant='body1'>
              Check-In Photos
            </Typography>
          </Grid>

          <CheckInReviewItem
            complete={Boolean(manufacturersLabelPhoto.documentGuid)}
            label="Manufacturer's Label"
            warning={manufacturersLabelPhoto.vehicleCheckInIsManufacturersLabelPhotoMissing}
          />

          <CheckInReviewItem
            complete={Boolean(tirePressureLabelPhoto.documentGuid)}
            label='Tire Pressure Label'
            warning={tirePressureLabelPhoto.vehicleCheckInIsTirePressureLabelPhotoMissing}
          />

          <CheckInReviewItem
            complete={Boolean(importerDoorLabelPhoto.documentGuid)}
            disabled={vehicleCheckInDetails.convertCluster}
            label='Importer Door Label'
          />

          <CheckInReviewItem
            complete={Boolean(comboPhoto.documentGuid)}
            disabled={vehicleCheckInDetails.convertCluster}
            label='Combo'
          />

          <CheckInReviewItem
            complete={Boolean(checkInClusterPhoto.documentGuid)}
            label='Speedometer Before Modification'
          />

          <CheckInReviewItem
            complete={Boolean(clusterWithOdometerPhoto.documentGuid)}
            label='Odometer Before Modification'
          />

          <CheckInReviewItem
            complete={Boolean(driverAirbagPhoto.documentGuid)}
            label='Driver Airbag'
          />

          <CheckInReviewItem
            complete={Boolean(backupCamPhoto.documentGuid)}
            disabled={backupCamPhoto.vehicleCheckInHasBackupCam === false}
            label='Backup Cam'
          />

          <CheckInReviewItem
            complete={Boolean(airbagTellTalePhoto.documentGuid)}
            label='Airbag Tell-Tale'
          />

          <CheckInReviewItem
            complete={Boolean(passengerAirbagPhoto.documentGuid)}
            disabled={passengerAirbagPhoto.vehicleCheckInHasPassengerAirbag === false}
            label='Passenger Airbag'
          />

          <CheckInReviewItem
            complete={Boolean(vinOnDashboardPhoto.documentGuid)}
            label='VIN on Dashboard'
          />

          <CheckInReviewItem complete={Boolean(roofPhoto.documentGuid)} label='Roof' />

          <CheckInReviewItem complete={Boolean(frontCornerPhoto.documentGuid)} label='Front Corner' />

          <CheckInReviewItem
            complete={Boolean(frontHeadOnPhoto.documentGuid)}
            label='Front Head On'
          />

          <CheckInReviewItem complete={Boolean(rearCornerPhoto.documentGuid)} label='Rear Corner' />

          <CheckInReviewItem complete={Boolean(rearHeadOnPhoto.documentGuid)} label='Rear Head On' />

          <Grid item xs={12}>
            <ExplanationAccordion variant='outlined'>
              || The Vehicle Details section will always show a green checkmark (users must complete that screen before they can
              see this screen). || In the Print Labels section, Importer Door Label and Window Label will always show a green
              checkmark. || In the Print Labels section, Convert Cluster Label will show a green checkmark if Convert Cluster =
              Yes was selected during vehicle check in or a gray “N/A” if Convert Cluster = No was selected. || In the Print
              Labels section, Program Cluster Label will show a green checkmark if Program Cluster = Yes was selected during
              vehicle check in or a gray “N/A” if Program Cluster = No was selected. || In the Check-In Photos, Manufacturer’s
              Label will show a green checkmark if a photo was uploaded, a yellow exclamation if the Manufacturer’s Label was
              marked as “Missing”, or a red “X” if a photo was not uploaded. || In the Check-In Photos, Tire Pressure Label will
              show a green checkmark if a photo was uploaded, a yellow exclamation if the Tire Pressure Label was marked as
              “Missing” or a red “X” if a photo was not uploaded. || In the Check-In Photos, Importer Door Label will show a
              green checkmark if a photo was uploaded, a gray “N/A” if Convert Cluster = Yes was selected on the “Vehicle
              Details” step of vehicle check in, or a red “X” if a photo was not uploaded. || In the Check-In Photos, Combo will
              show a green checkmark if a photo was uploaded, a gray “N/A” if Convert Cluster = Yes was selected on the “Vehicle
              Details” step of vehicle check in, or a red “X” if a photo was not uploaded. || Speedometer Before Modification,
              Odometer Before Modification, Driver Airbag, Airbag tell-tale, VIN on Dashboard, Roof, Front corner, Front head on, Rear corner, and
              Rear head on will show a green checkmark if a photo was uploaded or a red “X” if a photo was not uploaded. ||
              For Check-In Photos, Backup Cam will show a green checkmark if a photo was uploaded, a gray “N/A” if Vehicle
              does not have Backup Cam was selected during vehicle check in photo capture, or a red “X” if a photo was not
              uploaded. || For Check-In Photos, Passenger Airbag will show a green checkmark if a photo was uploaded, a gray
              “N/A” if Vehicle does not have Passenger Airbag was selected during vehicle check in, or a red “X” if a photo was
              not uploaded. ||
            </ExplanationAccordion>
          </Grid>

          <Grid item xs={12}>
            <Button color='primary' fullWidth onClick={onSubmitAndRecordVehicleDamage} variant='outlined'>
              SUBMIT & RECORD VEHICLE DAMAGE
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button color='primary' endIcon={<ArrowRight />} fullWidth onClick={onSubmit} variant='contained'>
              SUBMIT
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Button color='primary' startIcon={<ArrowLeft />} fullWidth onClick={onBack} variant='outlined'>
              PREVIOUS
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
